import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, Col, Row, Table, Badge } from 'reactstrap';
import ArrowUpIcon from 'mdi-react/ArrowUpIcon';
import ArrowDownIcon from 'mdi-react/ArrowDownIcon';
import EditOutlineIcon from 'mdi-react/EditOutlineIcon';
import ContentCopyIcon from 'mdi-react/ContentCopyIcon';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import moment from 'moment';
import Loading from '../../shared/components/custom/Loading';
import useModal from '../../shared/hooks/useModal';
import Api from '../../util/api';
import DeleteModal from '../../shared/components/custom/DeleteModal';
import Pagination from '../../shared/components/pagination/Pagination';
import t, { partial } from '../../util/translation/translation';
import { formatDateTime, formatDate } from '../../util/date-and-time';
import FormCard from '../../shared/components/custom/FormCard';
import SquareIconButton from '../../shared/components/custom/SquareIconButton';

const QuestionaireListPage = ({ setLoading }) => {
  const history = useHistory();
  const [questionaires, setQuestionaires] = useState([]);
  const [handleModal, visible, deleteID] = useModal();
  const [pagination, setPagination] = useState({
    total: 0,
    currentPage: 1,
    perPage: 10,
  });
  const [order, setOrder] = useState({
    orderBy: 'createdAt',
    desc: true,
  });
  const UNLIMITED_DATE = '9999-12-31T00:00:00.000Z';
  const p = partial('questionaire');
  const s = partial('shared');

  const fetchQuestionaires = async (
    currentPage = 1,
    pageSize = 10,
    orderObject = {
      orderBy: 'createdAt',
      desc: true,
    }
  ) => {
    setLoading(true);
    try {
      const reqQuestionaires = await Api.questionaire.getQuestionaires(
        { page: currentPage, take: pageSize },
        {
          orderBy: orderObject.orderBy,
          direction: orderObject.desc ? 'DESC' : 'ASC',
        }
      );
      setQuestionaires(reqQuestionaires.data);
      setPagination((old) => ({ ...old, total: reqQuestionaires.count }));
      setLoading(false);
      const questionaireIds = reqQuestionaires.data.map((item) => item.id);
      const { data: viewsData } =
        await Api.questionaire.getQuestionairesViewAmount({ questionaireIds });
      const mapped = reqQuestionaires.data.map((item) => ({
        ...item,
        views:
          viewsData.find((itemView) => itemView.itemId === item.id)?.views || 0,
      }));
      setQuestionaires(mapped);
    } catch (err) {
      toast.error(p('failedToFetchQuestionaires'));
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchQuestionaires();
  }, []);
  const deleteQuestionaire = async () => {
    setLoading(true);
    try {
      await Api.questionaire.deleteQuestionaire(deleteID);
      toast.success(p('questionaireDeleted'));
    } catch (err) {
      toast.error(p('questionaireDeleteError'));
    } finally {
      handleModal();
      fetchQuestionaires(pagination.currentPage, pagination.perPage, order);
    }
  };

  const onChangePage = (pager) => {
    if (
      pager.pageSize !== pagination.perPage ||
      pager.currentPage !== pagination.currentPage
    ) {
      setPagination((old) => ({
        ...old,
        currentPage: pager.currentPage,
        perPage: pager.pageSize,
      }));
      fetchQuestionaires(pager.currentPage, pager.pageSize, order);
    }
  };

  const renderSort = (cellOrder) => {
    if (order.orderBy !== cellOrder) {
      return undefined;
    }
    return order.desc ? <ArrowDownIcon size={16} /> : <ArrowUpIcon size={16} />;
  };
  const handleSort = (incommingOrder) => {
    const newOrder = {
      orderBy: incommingOrder,
      desc: incommingOrder === order.orderBy ? !order.desc : true,
    };
    setOrder(newOrder);
    setPagination({
      total: 0,
      currentPage: 1,
      perPage: 10,
    });
    fetchQuestionaires(1, 10, newOrder);
  };
  const getAvailableFor = (questionaire) => {
    const { access } = questionaire;
    if (!access) {
      return s('all');
    }
    if (access.levels || access.groups || access.tiers || access.segments) {
      return t('OfferForm.targetAudience');
    }
    return s('all');
  };
  const getBadgeStatus = (questionaire) => {
    if (!questionaire.visible) {
      return {
        color: 'danger',
        text: s('inActive'),
      };
    }
    if (moment().isAfter(moment(questionaire.endDate))) {
      return {
        color: 'secondary',
        text: s('closed'),
      };
    }
    if (moment().isBefore(moment(questionaire.startDate))) {
      return {
        color: 'warning',
        text: s('scheduled'),
      };
    }
    return {
      color: 'success',
      text: s('active'),
    };
  };

  const renderQuestionaires = () => {
    return questionaires.map((questionaire, i) => {
      const badge = getBadgeStatus(questionaire);
      return (
        <tr
          style={{ background: i % 2 ? '#fff' : '#F9FAFB' }}
          key={`questionaire-${questionaire.id}`}
        >
          <td>
            <div className="flex-Vcenter">
              <Link to={`/questionnaires/edit/${questionaire.id}`}>
                {questionaire.title}
              </Link>
            </div>
          </td>
          <td>
            <div>
              {formatDateTime(questionaire.startDate)} -{' '}
              {questionaire.endDate === UNLIMITED_DATE
                ? ''
                : formatDateTime(questionaire.endDate)}
            </div>
            <small className="text-muted">
              {s('created')}: {formatDate(questionaire.createdAt)}
            </small>
          </td>
          <td>{getAvailableFor(questionaire)}</td>
          <td className="text-center">
            {questionaire.views !== undefined ? (
              <div>{questionaire.views}</div>
            ) : (
              <Loading loading size={40} />
            )}
            <small className="text-muted">
              {questionaire.answersCount} {p('responses')}
            </small>
          </td>
          <td>{p(`questionaireType-${questionaire.type}`)}</td>
          <td>
            <Badge color={badge.color}>{badge.text}</Badge>
          </td>
          <td>
            <div className="icon-actions">
              <SquareIconButton
                icon={<EditOutlineIcon />}
                onClick={() =>
                  history.push(`/questionnaires/edit/${questionaire.id}`)
                }
                title={s('edit')}
                id={`edit-${questionaire.id}`}
              />
              <SquareIconButton
                icon={<ContentCopyIcon />}
                onClick={() =>
                  history.push(`/questionnaires/clone/${questionaire.id}`)
                }
                title={s('clone')}
                id={`clone-${questionaire.id}`}
              />
              <SquareIconButton
                icon={<DeleteOutlineIcon />}
                onClick={() => handleModal(questionaire.id)}
                title={s('delete')}
                tone="danger"
                id={`delete-${questionaire.id}`}
              />
            </div>
          </td>
        </tr>
      );
    });
  };

  return (
    <>
      <DeleteModal
        visible={visible}
        type={p('theQuestionaire')}
        handleModal={handleModal}
        modalAction={deleteQuestionaire}
      />
      <Row>
        <Col>
          <FormCard
            title={p('questionaires')}
            actions={
              <Link to="/questionnaires/create">
                <Button size="sm" color="primary" className="mb-0">
                  {s('create')}
                </Button>
              </Link>
            }
            bodyClassName="form-card__body--flush"
            className="mb-3"
          >
            <Table responsive striped className="table--roomy">
              <thead>
                <tr>
                  <th
                    style={{ color: '#4D576A' }}
                    className="sortable"
                    onClick={() => handleSort('title')}
                  >
                    <div className="flex alignItems-center">
                      {s('title')} {renderSort('title')}
                    </div>
                  </th>
                  <th
                    style={{ color: '#4D576A' }}
                    className="sortable"
                    onClick={() => handleSort('createdAt')}
                  >
                    <div className="flex alignItems-center">
                      {s('date')} {renderSort('createdAt')}
                    </div>
                  </th>
                  <th style={{ color: '#4D576A' }}>
                    <div className="flex alignItems-center">
                      {t('EventsForm.availableFor')}
                    </div>
                  </th>
                  <th
                    style={{ color: '#4D576A' }}
                    className="sortable"
                    onClick={() => handleSort('answersCount')}
                  >
                    <div className="flex alignItems-center">
                      {s('views')} / {p('responses')}{' '}
                      {renderSort('answersCount')}
                    </div>
                  </th>
                  <th style={{ color: '#4D576A' }}>
                    <div className="flex alignItems-center">{s('type')}</div>
                  </th>
                  <th style={{ color: '#4D576A' }}>
                    <div className="flex alignItems-center">{s('status')}</div>
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {questionaires.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-muted">
                      {p('noQuestionaires')}
                    </td>
                  </tr>
                ) : (
                  renderQuestionaires()
                )}
              </tbody>
            </Table>
          </FormCard>
          <Pagination
            items={pagination.total}
            onChangePage={onChangePage}
            initialPage={pagination.currentPage}
          />
        </Col>
      </Row>
    </>
  );
};

export default QuestionaireListPage;
